import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { NewVersionDialog } from "@/components/NewVersionDialog";
import { Button } from "@/components/ui/button";
import { ChevronRight, Trash2 } from "lucide-react";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { useUser } from "../../context/UserContext";

interface Version {
  id: number;
  name: string;
  description: string | null;
  files_count: number;
  total_lines_count: number;
  translated_lines_count: number;
  total_chars_count?: number;
  translated_chars_count?: number;
}

interface Game {
  id: number;
  name: string;
  description: string;
  image_url: string | null;
  game_versions: Version[];
}

const GameDetails = () => {
  const { gameId } = useParams();
  const [game, setGame] = useState<Game | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useUser();

  const fetchGame = async () => {
    try {
      const response = await fetch(`/api/games/${gameId}`);
      if (!response.ok) {
        throw new Error("A játék betöltése sikertelen volt.");
      }
      const data = await response.json();
      setGame({
        ...data,
        game_versions: data.game_versions || [], // Biztosítjuk, hogy a game_versions mindig tömb legyen
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Ismeretlen hiba történt.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGame();
  }, [gameId]);

  const handleVersionAdded = (newVersion: Version) => {
    setGame(prevGame => {
      if (!prevGame) return null;
      return {
        ...prevGame,
        game_versions: [newVersion, ...prevGame.game_versions],
      };
    });
  };

  const handleDeleteVersion = async (versionId: number) => {
    if (window.confirm("Biztosan törölni szeretnéd ezt a verziót? Ez a művelet nem vonható vissza.")) {
      try {
        const csrfToken = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content;
        const response = await fetch(`/api/games/${gameId}/versions/${versionId}`, {
          method: 'DELETE',
          headers: {
            'X-CSRF-Token': csrfToken,
          },
        });

        if (!response.ok) {
          throw new Error("A verzió törlése sikertelen volt.");
        }

        setGame(prevGame => {
          if (!prevGame) return null;
          return {
            ...prevGame,
            game_versions: prevGame.game_versions.filter(v => v.id !== versionId),
          };
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : "Ismeretlen hiba történt a törlés során.");
      }
    }
  };

  if (loading) return <div>Betöltés...</div>;
  if (error) return <div>Hiba: {error}</div>;

  if (!game) {
    return <div>A játék nem található.</div>;
  }

  return (
    <div>

      <Breadcrumb className="mb-4">
        <BreadcrumbList>
          <BreadcrumbItem><BreadcrumbLink asChild><Link to="/games">Játékok</Link></BreadcrumbLink></BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>{game.name}</BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
      <div className="flex items-center gap-4 mb-6">
        {game.image_url && <img src={game.image_url} alt={game.name} className="w-24 h-24 rounded-lg object-cover" />}
        <div>
          <h1 className="text-4xl font-bold">{game.name}</h1>
          <p className="text-lg text-muted-foreground">{game.description}</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>Verziók</CardTitle>
            {user?.admin && (
              <NewVersionDialog gameId={game.id} versions={game.game_versions || []} onVersionAdded={handleVersionAdded} />
            )}
          </div>
        </CardHeader>
        <CardContent>
          {game.game_versions && game.game_versions.length > 0 ? (
            <ul className="space-y-2">
              {game.game_versions.map((version) => (
                <li key={version.id} className="flex justify-between items-center p-3 border rounded-md hover:bg-accent transition-colors">
                  <Link to={`/games/${game.id}/versions/${version.id}`} className="flex-grow">
                    <div>
                      <span className="font-semibold">{version.name}</span>
                      {version.description && <p className="text-sm text-muted-foreground">{version.description}</p>}
                      <div className="text-sm text-muted-foreground mt-2">
                        <p>Fájlok: {version.files_count}</p>
                        <p>Sorok: {version.total_lines_count}</p>
                        <p>Lefordítva: {version.translated_lines_count} / {version.total_lines_count}</p>
                        {typeof version.total_chars_count !== 'undefined' && (
                          <p>Karakterek: {version.translated_chars_count ?? 0} / {version.total_chars_count}</p>
                        )}
                      </div>
                    </div>
                  </Link>
                  <div className="flex items-center ml-4">
                    {user?.admin && (
                      <Button variant="ghost" size="icon" onClick={() => handleDeleteVersion(version.id)}>
                        <Trash2 className="h-5 w-5 text-red-500" />
                      </Button>
                    )}
                    <Link to={`/games/${game.id}/versions/${version.id}`}>
                      <ChevronRight className="h-5 w-5 text-muted-foreground" />
                    </Link>
                  </div>
                </li>
              ))}
            </ul>
          ) : <p className="text-muted-foreground">Nincsenek még verziók ehhez a játékhoz.</p>}
        </CardContent>
      </Card>
    </div>
  );
};

export default GameDetails;